import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import dayjs from "dayjs";
import { Check, Printer, X, ArrowLeft } from "lucide-react";
// eslint-disable-next-line import/no-unresolved
import "@oppizi-ecosystem/meeting/webcomponent";
// eslint-disable-next-line import/no-unresolved
import "@oppizi-ecosystem/meeting/styles.css";
import CONFIG from "config/config";
import { isProduction } from "utils";
import {
  Sheet,
  SheetBody,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "campaign-builder/ui";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { updateCampaign } from "graphQL/mutations/campaign";
import { useCouponCode } from "campaign-builder/hooks/useCouponCode";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import type { ChannelConfig } from "campaign-builder/config";
import { getFlyerFormatMeta, formatSizeLabel, useDesignSelection } from "campaign-builder/channels/flyering/design";
import { usePostHog } from "posthog-js/react";
// HUB-440: reuse the ADM review-for-discount components as-is (they are not
// ADM-coupled — they hit the eligibility endpoint by client and render a row).
// TODO(HUB-440): lift this reviewOffer folder to a shared, channel-agnostic path
// (e.g. campaign-builder/checkout/reviewOffer) so flyering no longer reaches
// across into the directmail/adm tree; keep this import in sync when that lands.
import {
  ReviewOfferRow,
  useReviewDiscountSummary,
  useDiscountExclusivity,
  DiscountSwapDialog,
  ReviewOfferEntitlement,
  DiscountChoice,
} from "campaign-builder/channels/directmail/adm/order-details/reviewOffer";
import { getReviewOfferCopy } from "campaign-builder/channels/directmail/adm/order-details/reviewOffer/reviewOfferCopy";
import { DEFAULT_REVIEW_OFFER_PROGRAM } from "campaign-builder/channels/directmail/adm/order-details/reviewOffer/types";
import { useTargeting } from "../targeting";
import { parseScheduleDate } from "campaign-builder/state/h2hSchedule";
import { useChannelCostPreview } from "./cost/useChannelCostPreview";
import { TermsAgreementNote } from "campaign-builder/checkout/TermsAgreementNote";

interface OrderDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  config: ChannelConfig;
  isPaymentSkipped: boolean;
  onGoToPayment: () => void;
  /** F13 turns the Purchase Order entry on; off for F12. */
  showPurchaseOrder?: boolean;
  /** Billing-profile gate banner; the wall is at payment, not here. */
  topBanner?: React.ReactNode;
  /**
   * HUB-440: lifts useDiscountExclusivity's discountChoice up to the submit flow
   * (useChannelSubmit), which needs it to send `discount_choice` / apply the
   * review discount on the billing preview + charge. Mirrors ADM's
   * OrderDetailsDrawer.onDiscountChoiceChange.
   */
  onDiscountChoiceChange?: (choice: DiscountChoice) => void;
}

// Figma: h36 / px16 / radius8 / border #E2E8F0 / bg #FFF / shadow 0 1px 2px rgba(0,0,0,.05)
const BTN_SECONDARY =
  "box-border flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-solid border-border bg-background px-4 text-sm font-medium text-foreground shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-slate-50 dark:hover:bg-accent";
const BTN_PRIMARY =
  "box-border flex h-9 shrink-0 items-center justify-center rounded-lg bg-[#ef2b55] px-4 text-sm font-medium text-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-[#ef2b55]/90";

const money = (value: number | null | undefined, currencySign = "$") =>
  `${currencySign}${(value ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ApplyButton = ({
  onClick,
  disabled,
  "data-cypress-id": dataCypressId,
}: {
  onClick: () => void;
  disabled?: boolean;
  "data-cypress-id"?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label="Apply"
    className="box-border flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#ef2b55] text-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] disabled:opacity-50"
    data-cypress-id={dataCypressId}
  >
    <Check className="h-4 w-4" />
  </button>
);

const InlineInput = ({
  value,
  onChange,
  onClear,
  placeholder,
  "data-cypress-id": dataCypressId,
}: {
  value: string;
  onChange: (v: string) => void;
  onClear: () => void;
  placeholder: string;
  "data-cypress-id"?: string;
}) => (
  <div className="box-border flex h-9 flex-1 items-center rounded-lg border border-solid border-border bg-background pl-3 pr-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]">
    <input
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="text-foreground placeholder:text-muted-foreground flex-1 border-0 bg-transparent text-sm outline-none"
      data-cypress-id={dataCypressId}
    />
    <button
      type="button"
      onClick={onClear}
      aria-label="Clear"
      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 shrink-0"
      data-cypress-id={dataCypressId ? `${dataCypressId}Clear` : undefined}
    >
      <X className="h-4 w-4" />
    </button>
  </div>
);

const DetailRow = ({
  label,
  value,
  "data-cypress-id": dataCypressId,
}: {
  label: string;
  value: React.ReactNode;
  "data-cypress-id"?: string;
}) => (
  <div className="flex items-start justify-between gap-4 border-b border-solid border-border py-3 last:border-b-0">
    <span className="text-muted-foreground text-sm leading-5">{label}</span>
    <span className="text-foreground text-right text-sm font-medium leading-5" data-cypress-id={dataCypressId}>
      {value}
    </span>
  </div>
);

// H2H launch = earliest scheduled distribution date; D2D = the single launch
// date persisted on the campaign.
const resolveLaunchDate = (isD2D: boolean, startDate: string | null | undefined, areaDates: string[]) => {
  if (isD2D) {
    return startDate ? dayjs(Number(startDate)).format("MMM D, YYYY") : "—";
  }
  const earliest = areaDates
    .map((d) => parseScheduleDate(d)?.getTime())
    .filter((t): t is number => t !== undefined)
    .sort((a, b) => a - b)[0];
  return earliest ? dayjs(earliest).format("MMM D, YYYY") : "—";
};

export const OrderDetailsDrawer = ({
  open,
  onClose,
  config,
  isPaymentSkipped,
  onGoToPayment,
  showPurchaseOrder = false,
  topBanner,
  onDiscountChoiceChange,
}: OrderDetailsDrawerProps) => {
  const { campaignData, refetchCampaign, user } = useCampaignData();
  const { country, client } = useClientConfig();
  const { selectedAreas } = useTargeting();
  const { selectedDesign } = useDesignSelection();
  const quoteItemLabel = config.orderDetails?.quoteItemLabel ?? config.label;
  const { campaignCostPreview, isLoadingCost } = useChannelCostPreview(quoteItemLabel);
  // Block payment until the server cost has loaded — otherwise the quote shows
  // $0.00 and submitting would persist a zero-dollar campaign record. A zeroed
  // breakdown counts as not-loaded: the server answers all-zeros (not an error)
  // when the persisted selection isn't in place yet, and no real quote for these
  // channels is ever $0.
  const costReady = !isLoadingCost && campaignCostPreview.lines.length > 0 && campaignCostPreview.subtotal > 0;

  const [view, setView] = useState<"order" | "meeting">("order");
  useEffect(() => {
    if (open) setView("order");
  }, [open]);

  const onUpdateCampaignCostWithCoupon = async (couponCode: string | null, amountOff: number, percentOff: number) => {
    await updateCampaignExtraData({
      campaignId: campaignData?.id,
      stripeCouponCode: couponCode,
      amountOff,
      percentOff,
    });
    // The server breakdown is coupon-independent; the preview recomputes from the
    // refreshed campaign coupon fields, so no cost refetch is needed.
    await refetchCampaign();
  };

  const coupon = useCouponCode({
    campaign: campaignData as any,
    country: country as any,
    campaignCostPreview,
    onUpdateCampaignCostWithCoupon,
    client: client as any,
  });
  const [couponInput, setCouponInput] = useState("");

  // Purchase Order Number (F13 — rendered only when showPurchaseOrder). Persists
  // onto the campaign via updateCampaign (channel-agnostic column).
  const [poOpen, setPoOpen] = useState(false);
  const [poInput, setPoInput] = useState("");
  const [poError, setPoError] = useState<string | null>(null);
  const [isSavingPo, setIsSavingPo] = useState(false);
  const savingPoRef = useRef(false);
  const savedPo = campaignData?.purchaseOrderNumber || "";

  // Reset the PO editing state each time the drawer opens, so a dismissed,
  // unsaved edit doesn't reappear — or auto-save via go-to-payment — next open.
  useEffect(() => {
    if (open) {
      setPoOpen(false);
      setPoInput("");
      setPoError(null);
    }
  }, [open]);

  const persistPo = useCallback(
    async (value: string) => {
      const id = campaignData?.id;
      if (!id) throw new Error("Missing campaign id");
      await updateCampaign({ campaignId: id, purchaseOrderNumber: value || null });
      await refetchCampaign();
    },
    [campaignData?.id, refetchCampaign]
  );

  const handleApplyPo = async () => {
    if (savingPoRef.current) return;
    const value = poInput.trim();
    if (!value && !savedPo) {
      setPoOpen(false);
      setPoInput("");
      return;
    }
    savingPoRef.current = true;
    setIsSavingPo(true);
    try {
      await persistPo(value);
      setPoError(null);
      setPoOpen(false);
      setPoInput("");
    } catch (error) {
      console.error("[OrderDetailsDrawer] Failed to save Purchase Order Number", error);
      setPoError("Couldn't save the PO number. Please try again.");
    } finally {
      savingPoRef.current = false;
      setIsSavingPo(false);
    }
  };

  const handleGoToPayment = async () => {
    if (savingPoRef.current) return;
    const pending = poInput.trim();
    if (showPurchaseOrder && poOpen && pending !== savedPo) {
      savingPoRef.current = true;
      setIsSavingPo(true);
      try {
        await persistPo(pending);
        setPoError(null);
        setPoOpen(false);
        setPoInput("");
      } catch (error) {
        console.error("[OrderDetailsDrawer] Failed to save Purchase Order Number before payment", error);
        setPoError("Couldn't save the PO number. Please try again.");
        return;
      } finally {
        savingPoRef.current = false;
        setIsSavingPo(false);
      }
    }
    onGoToPayment();
  };

  const isD2D = config.channel === "D2D";
  const areaDates = useMemo(
    () => selectedAreas.flatMap((area) => (area.schedule ?? []).filter((e) => e.times.length).map((e) => e.date)),
    [selectedAreas]
  );
  const launchDate = resolveLaunchDate(isD2D, campaignData?.startDate, areaDates);
  const distributionAreas = selectedAreas.length ? selectedAreas.map((area) => area.value).join(", ") : "—";

  const formatMeta = getFlyerFormatMeta(campaignData?.flyerType);
  // Format row shows label + dimensions only (price lives in the quote).
  const formatValue = formatMeta
    ? `${formatMeta.label} ${formatSizeLabel(formatMeta, country?.code).split(" · ")[0]}`
    : campaignData?.flyerType ?? "—";

  const total = campaignCostPreview.total ?? campaignCostPreview.subtotal;

  // HUB-440: review-for-discount offer, entirely behind is_review_discount_enabled
  // (the ReviewOfferRow checks the flag at its own top-level render). Mirrors ADM's
  // OrderDetailsDrawer wiring exactly — "one discount per order": review wins over a
  // coupon, with the swap dialog handling the exclusivity handoff.
  const posthog = usePostHog();
  const [reviewEntitlement, setReviewEntitlement] = useState<ReviewOfferEntitlement | null>(null);
  const hasStripeCoupon = !!campaignData?.stripeCouponCode;
  // HUB-440: a skip-checkout client (invoiced/managed) never reaches the
  // /billing charge, so the review discount can't be applied — treat them as
  // having no entitlement here so the exclusivity, the discounted total, and
  // the derived discount_choice all stay off (the offer row is also hidden
  // below). Prevents showing a discount the backend would never apply.
  const hasReviewEntitlement = !isPaymentSkipped && !!reviewEntitlement && reviewEntitlement.status === "active";

  const {
    discountChoice,
    swapDirection,
    isSwapping,
    swapError,
    isSwapBusy,
    openSwapDialog,
    startPendingReviewSwap,
    confirmSwap,
    cancelSwap,
  } = useDiscountExclusivity({
    hasStripeCoupon,
    hasReviewEntitlement,
    onClearCoupon: () => onUpdateCampaignCostWithCoupon(null, 0, 0),
    onSwapped: () =>
      posthog?.capture("review_discount_swapped", { event_category: "Quote", campaign_id: campaignData?.id }),
  });

  const reviewDiscount = useReviewDiscountSummary({
    entitlement: reviewEntitlement,
    subtotal: campaignCostPreview.subtotal,
    total,
    isReviewDiscountChosen: discountChoice === "review",
  });
  const displayTotal = reviewDiscount.isApplied ? reviewDiscount.displayTotal : total;

  // HUB-440: lift discountChoice up so useChannelSubmit can apply the review
  // discount on the billing preview + charge. Mirrors ADM's onDiscountChoiceChange.
  useEffect(() => {
    onDiscountChoiceChange?.(discountChoice);
  }, [discountChoice, onDiscountChoiceChange]);

  if (view === "meeting") {
    return (
      <Sheet open={open} onClose={onClose} aria-label="Book a review meeting" className="max-w-[600px]">
        <div className="flex items-center gap-3 border-b border-solid border-border p-6">
          <button
            type="button"
            onClick={() => setView("order")}
            aria-label="Back to order details"
            className="text-muted-foreground hover:text-foreground"
            data-cypress-id="orderDetailsBackToOrder"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold" data-cypress-id="bookReviewMeetingTitle">
            Book a review meeting
          </h2>
        </div>
        <SheetBody>
          <oppizi-meeting
            first-name={user?.firstName ?? ""}
            last-name={user?.lastName ?? ""}
            email={user?.email ?? ""}
            country={country?.code}
            redirect-url={`${CONFIG.PLATFORM_BASE_URL}/dashboard/home`}
            ui-mode="embedded"
            is-prod={isProduction()}
            data-cypress-id="orderDetailsBookMeetingEmbed"
          />
        </SheetBody>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onClose={onClose} aria-label="Order details" className="max-w-[600px]">
      <SheetHeader>
        <SheetTitle data-cypress-id="orderDetailsTitle">Order details</SheetTitle>
        <SheetDescription data-cypress-id="orderDetailsDescription">
          Review your order before submitting.
        </SheetDescription>
      </SheetHeader>

      <SheetBody className="space-y-6">
        {topBanner}
        {/* Quote (AC57-59) */}
        <section className="flex flex-col gap-2" data-cypress-id="orderDetailsQuote">
          <h3 className="text-foreground text-sm font-medium leading-5" data-cypress-id="quoteTitle">
            Quote
          </h3>
          <div className="overflow-hidden rounded-lg border border-solid border-border">
            <div className="flex items-center justify-between border-b border-solid border-border bg-muted px-4 py-4 text-sm font-medium">
              <span className="text-foreground" data-cypress-id="itemDescriptionTitle">
                Item description
              </span>
              <span className="text-muted-foreground flex gap-12">
                <span data-cypress-id="quantityTitle">Quantity</span>
                <span data-cypress-id="totalTitle">Total</span>
              </span>
            </div>
            {!costReady ? (
              <div className="flex flex-col gap-3 px-4 py-6" data-cypress-id="orderDetailsQuoteLoading">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : (
              <>
                {campaignCostPreview.lines.map((line) => (
                  <div
                    key={line.title}
                    className="flex items-center justify-between border-b border-solid border-border px-4 py-4 text-sm"
                  >
                    <span className="text-foreground" data-cypress-id="orderDetailsQuoteLineTitle">
                      {line.title}
                    </span>
                    <span className="flex items-center gap-12">
                      <span className="text-muted-foreground" data-cypress-id="orderDetailsQuoteQuantity">
                        {line.quantity.toLocaleString("en-US")}
                      </span>
                      <span className="text-foreground" data-cypress-id="orderDetailsQuoteTotal">
                        {money(line.price, country?.currencySign)}
                      </span>
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-end gap-12 border-b border-solid border-border px-4 py-4 text-sm">
                  <span className="text-muted-foreground" data-cypress-id="subtotalTitle">
                    Subtotal
                  </span>
                  <span className="text-foreground" data-cypress-id="orderDetailsSubtotal">
                    {money(campaignCostPreview.subtotal, country?.currencySign)}
                  </span>
                </div>
                {/* Review discount summary line (HUB-440) — between Subtotal and Total, mirroring ADM */}
                {reviewDiscount.isApplied && (
                  <div
                    className="flex h-12 items-center justify-between border-b border-solid border-border px-4 text-sm"
                    data-cypress-id="reviewOfferDiscountSummaryLine"
                  >
                    <span className="flex items-center gap-1.5 font-medium text-[#009244] dark:text-[#4ADE80]">
                      <Check className="h-3.5 w-3.5" />
                      {reviewDiscount.label}
                    </span>
                    <span className="font-semibold text-[#009244] dark:text-[#4ADE80]">
                      −{money(reviewDiscount.amountOff, country?.currencySign)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 bg-muted px-4 py-3">
                  {/* discount coupon (AC60-61) */}
                  <div className="flex flex-1 items-center gap-2">
                    {reviewDiscount.isApplied ? (
                      <button
                        type="button"
                        onClick={() => openSwapDialog("reviewToCoupon")}
                        title={
                          getReviewOfferCopy(reviewEntitlement?.program ?? DEFAULT_REVIEW_OFFER_PROGRAM).offer
                            .exclusiveTooltip
                        }
                        className={BTN_SECONDARY}
                        data-cypress-id="orderDetailsCouponDisabledByReviewDiscount"
                      >
                        I have a discount coupon
                      </button>
                    ) : campaignData?.stripeCouponCode ? (
                      <>
                        <span
                          className="text-foreground flex items-center gap-1.5 text-sm font-medium"
                          data-cypress-id="orderDetailsCouponCode"
                        >
                          {campaignData.stripeCouponCode}
                          <Check className="h-4 w-4 text-success-foreground" />
                        </span>
                        <button
                          type="button"
                          onClick={() => onUpdateCampaignCostWithCoupon(null, 0, 0)}
                          className={BTN_SECONDARY}
                          data-cypress-id="orderDetailsCouponRemove"
                        >
                          Remove
                        </button>
                      </>
                    ) : coupon.isCouponCodeOpen ? (
                      <>
                        <InlineInput
                          value={couponInput}
                          onChange={(v) => {
                            setCouponInput(v);
                            coupon.setCouponErrorMessage("");
                          }}
                          onClear={() => {
                            setCouponInput("");
                            coupon.setIsCouponCodeOpen(false);
                          }}
                          placeholder="Enter coupon code"
                          data-cypress-id="orderDetailsCouponInput"
                        />
                        <ApplyButton
                          onClick={() => coupon.handleApplyCouponCode({ stripeCouponCode: couponInput })}
                          data-cypress-id="orderDetailsCouponApply"
                        />
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={coupon.handleOpenCouponCode}
                        className={BTN_SECONDARY}
                        data-cypress-id="orderDetailsCouponOpen"
                      >
                        I have a discount coupon
                      </button>
                    )}
                  </div>
                  <div className="text-foreground flex items-center gap-12 text-sm">
                    <span className="font-medium" data-cypress-id="totalBeforeTaxTitle">
                      Total (Before Tax)
                    </span>
                    <span className="font-semibold" data-cypress-id="orderDetailsTotalBeforeTax">
                      {money(displayTotal, country?.currencySign)}
                    </span>
                  </div>
                </div>
                {/* HUB-440: never offer the review discount when checkout is
                    skipped — there's no payment for it to apply to. */}
                {!isPaymentSkipped && (
                  <ReviewOfferRow
                    campaignId={campaignData?.id}
                    countryCode={country?.code}
                    discountChoice={discountChoice}
                    onRequestSwapToReview={() => openSwapDialog("couponToReview")}
                    onStartPendingReviewSwap={startPendingReviewSwap}
                    isSwapBusy={isSwapBusy}
                    onEntitlementChange={setReviewEntitlement}
                  />
                )}
              </>
            )}
          </div>
          {costReady && coupon.couponErrorMessage && (
            <p className="text-xs text-destructive" data-cypress-id="couponErrorMessage">
              {coupon.couponErrorMessage}
            </p>
          )}
        </section>

        {/* HUB-440: "one discount per order" swap confirmation — program/percent come
            from the entitlement being swapped, never a hardcoded value. */}
        <DiscountSwapDialog
          direction={swapDirection}
          program={reviewEntitlement?.program}
          percent={reviewEntitlement?.discount_percent}
          isSubmitting={isSwapping}
          hasError={swapError}
          onCancel={cancelSwap}
          onConfirm={confirmSwap}
        />

        {/* Campaign specifics (AC63) */}
        <section
          className="overflow-hidden rounded-lg border border-solid border-border"
          data-cypress-id="orderDetailsCampaignSpecifics"
        >
          <div
            className="text-foreground border-b border-solid border-border bg-muted px-4 py-3 text-sm font-medium"
            data-cypress-id="campaignSpecificsTitle"
          >
            Campaign specifics
          </div>
          <div className="px-4">
            <DetailRow
              label="Campaign name"
              value={campaignData?.campaignName || "—"}
              data-cypress-id="orderDetailsCampaignName"
            />
            <DetailRow label="Launch date" value={launchDate} data-cypress-id="orderDetailsLaunchDate" />
            <DetailRow
              label="Distribution areas"
              value={distributionAreas}
              data-cypress-id="orderDetailsDistributionAreas"
            />
            <DetailRow label="Format" value={formatValue} data-cypress-id="orderDetailsFormat" />
          </div>
        </section>

        {/* PO number (F13) + Print (AC66) */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            {showPurchaseOrder &&
              (savedPo && !poOpen ? (
                <button
                  type="button"
                  onClick={() => {
                    setPoError(null);
                    setPoInput(savedPo);
                    setPoOpen(true);
                  }}
                  className={`${BTN_SECONDARY} flex-1 justify-start`}
                  data-cypress-id="orderDetailsPoSaved"
                >
                  PO Number: {savedPo}
                </button>
              ) : poOpen ? (
                <div className="flex flex-1 items-center gap-2">
                  <InlineInput
                    value={poInput}
                    onChange={setPoInput}
                    onClear={() => {
                      setPoError(null);
                      setPoInput("");
                      setPoOpen(false);
                    }}
                    placeholder="Enter Purchase Order Number"
                    data-cypress-id="orderDetailsPoInput"
                  />
                  <ApplyButton onClick={handleApplyPo} disabled={isSavingPo} data-cypress-id="orderDetailsPoApply" />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setPoError(null);
                    setPoInput("");
                    setPoOpen(true);
                  }}
                  className={BTN_SECONDARY}
                  data-cypress-id="orderDetailsPoAdd"
                >
                  Add Purchase Order Number
                </button>
              ))}
            <button
              type="button"
              onClick={() => window.print()}
              className={BTN_SECONDARY}
              data-cypress-id="orderDetailsPrint"
            >
              <Printer className="h-4 w-4" />
              Print order details
            </button>
          </div>
          {showPurchaseOrder && poError && (
            <p className="text-destructive text-xs leading-4" data-cypress-id="orderDetailsPoError">
              {poError}
            </p>
          )}
        </div>

        {/* Design preview (AC67) */}
        {selectedDesign?.thumbnailUrl && (
          <section className="flex flex-col gap-2" data-cypress-id="orderDetailsDesignPreview">
            <h3 className="text-foreground text-sm font-medium leading-5" data-cypress-id="designPreviewTitle">
              Design Preview
            </h3>
            <div className="overflow-hidden rounded-lg border border-solid border-border">
              <img
                src={selectedDesign.thumbnailUrl}
                alt={selectedDesign.designName}
                className="w-full"
                data-cypress-id="orderDetailsDesignPreviewImage"
              />
            </div>
          </section>
        )}
      </SheetBody>

      <SheetFooter className="flex flex-col gap-3 border-t border-solid border-border">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex flex-col">
            <span
              className="text-foreground text-base font-semibold leading-6"
              data-cypress-id="orderDetailsFooterTotal"
            >
              {money(displayTotal, country?.currencySign)}
            </span>
            <span className="text-muted-foreground text-xs leading-4" data-cypress-id="totalCostsBeforeTaxesTitle">
              Total costs before taxes
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setView("meeting")}
              className={BTN_SECONDARY}
              data-cypress-id="orderDetailsBookReviewMeeting"
            >
              Book a review meeting
            </button>
            <button
              type="button"
              onClick={handleGoToPayment}
              disabled={isSavingPo || !costReady}
              className={`${BTN_PRIMARY} disabled:opacity-50`}
              data-cypress-id="orderDetailsGoToPayment"
            >
              {isPaymentSkipped ? "Submit" : "Go to payment"}
            </button>
          </div>
        </div>
        <TermsAgreementNote actionLabel={isPaymentSkipped ? "Submit" : "Go to payment"} className="w-full text-right" />
      </SheetFooter>
    </Sheet>
  );
};
